import React, { useState, useEffect } from 'react';
import './FloorManagement.css';

const FloorManagement = ({ businessId }) => {
  const [floorAssignments, setFloorAssignments] = useState([
    { floorName: 'Ground Floor', fromTable: 1, toTable: 10 }
  ]);
  const [totalTables, setTotalTables] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [floors, setFloors] = useState([]);

  const API_BASE = process.env.REACT_APP_API_URL;

  // Load existing floors and total tables
  useEffect(() => {
    loadFloors();
    loadTotalTables();
  }, [businessId]);

  const loadTotalTables = async () => {
    if (!businessId) return;

    try {
      const response = await fetch(`${API_BASE}/qr-billing/dashboard`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Business-ID': businessId
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        // The API returns data inside result.data.tables
        const tables = result.data?.tables || result.tables || [];
        
        // Count only regular tables (exclude OWNER and TAKEAWAY)
        const regularTables = tables.filter(table => 
          table.tableNumber !== 'OWNER' && 
          table.tableNumber !== 'TAKEAWAY'
        );
        
        setTotalTables(regularTables.length);
      } else {
        console.error('❌ Dashboard API returned error:', response.status);
      }
    } catch (error) {
      console.error('❌ Error loading total tables:', error);
    }
  };

  const loadFloors = async () => {
    if (!businessId) return;

    try {
      const response = await fetch(`${API_BASE}/qr-billing/floors`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Business-ID': businessId
        }
      });

      if (response.ok) {
        const result = await response.json();
        setFloors(result.floors);
        
        // Convert floors data to floor assignments format with actual ranges
        if (result.floors && result.floors.length > 0) {
          const assignments = result.floors.map((floor) => ({
            floorName: floor.floor,
            fromTable: floor.min_table || 1,
            toTable: floor.max_table || 10
          }));
          
          setFloorAssignments(assignments);
        }
      }
    } catch (error) {
      console.error('❌ Error loading floors:', error);
    }
  };

  const addFloor = () => {
    const lastFloor = floorAssignments[floorAssignments.length - 1];
    const newFromTable = lastFloor ? lastFloor.toTable + 1 : 1;
    
    setFloorAssignments([
      ...floorAssignments,
      { 
        floorName: `Floor ${floorAssignments.length + 1}`, 
        fromTable: newFromTable, 
        toTable: newFromTable + 9 
      }
    ]);
  };

  const updateFloorAssignment = (index, field, value) => {
    const updated = [...floorAssignments];
    
    if (field === 'fromTable' || field === 'toTable') {
      // Allow empty string during typing
      if (value === '' || value === null) {
        updated[index][field] = '';
      } else {
        const numValue = parseInt(value);
        if (!isNaN(numValue)) {
          updated[index][field] = numValue;
        }
      }
    } else {
      updated[index][field] = value;
    }
    
    setFloorAssignments(updated);
  };

  const showError = (message) => {
    setErrorMessage(message);
    setShowErrorPopup(true);
    setTimeout(() => {
      setShowErrorPopup(false);
    }, 4000);
  };

  const removeFloor = (index) => {
    if (floorAssignments.length === 1) {
      showError('Cannot remove the last floor. At least one floor is required.');
      return;
    }
    
    const updated = floorAssignments.filter((_, i) => i !== index);
    setFloorAssignments(updated);
  };

  const saveFloorAssignments = async () => {
    if (!businessId) {
      showError('Business ID is required');
      return;
    }

    // Validation
    for (let i = 0; i < floorAssignments.length; i++) {
      const floor = floorAssignments[i];
      
      if (!floor.floorName || floor.floorName.trim() === '') {
        showError(`Floor ${i + 1}: Floor name is required`);
        return;
      }
      
      if (floor.fromTable > floor.toTable) {
        showError(`Floor ${i + 1}: "From Table" cannot be greater than "To Table"`);
        return;
      }

      // Check if table numbers exceed total tables
      if (floor.fromTable > totalTables) {
        showError(`Floor ${i + 1}: "From Table" (${floor.fromTable}) exceeds total tables in restaurant (${totalTables})`);
        return;
      }

      if (floor.toTable > totalTables) {
        showError(`Floor ${i + 1}: "To Table" (${floor.toTable}) exceeds total tables in restaurant (${totalTables})`);
        return;
      }
    }

    // Check for overlapping table ranges
    for (let i = 0; i < floorAssignments.length; i++) {
      for (let j = i + 1; j < floorAssignments.length; j++) {
        const floor1 = floorAssignments[i];
        const floor2 = floorAssignments[j];
        
        // Check if ranges overlap
        if (
          (floor1.fromTable >= floor2.fromTable && floor1.fromTable <= floor2.toTable) ||
          (floor1.toTable >= floor2.fromTable && floor1.toTable <= floor2.toTable) ||
          (floor2.fromTable >= floor1.fromTable && floor2.fromTable <= floor1.toTable) ||
          (floor2.toTable >= floor1.fromTable && floor2.toTable <= floor1.toTable)
        ) {
          showError(`Table range conflict between "${floor1.floorName}" (${floor1.fromTable}-${floor1.toTable}) and "${floor2.floorName}" (${floor2.fromTable}-${floor2.toTable})`);
          return;
        }
      }
    }

    // Calculate total tables assigned
    const totalAssigned = floorAssignments.reduce((sum, floor) => {
      return sum + (floor.toTable - floor.fromTable + 1);
    }, 0);

    // Check if total assigned exceeds total tables
    if (totalAssigned > totalTables) {
      showError(`Total tables assigned (${totalAssigned}) exceeds total tables in restaurant (${totalTables}). Please check the table ranges.`);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/qr-billing/floors/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Business-ID': businessId
        },
        body: JSON.stringify({
          assignments: floorAssignments
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        
        // Show success popup
        setShowSuccessPopup(true);
        setTimeout(() => {
          setShowSuccessPopup(false);
        }, 3000);

        // Reload floors
        await loadFloors();
      } else {
        showError(result.error || 'Failed to save floor assignments');
      }
    } catch (error) {
      console.error('❌ Error saving floor assignments:', error);
      showError('Failed to save floor assignments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const cancelChanges = () => {
    // Reload original floors from backend to discard unsaved edits
    loadFloors();
  };

  return (
    <div className="floor-management">
      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="floor-success-popup">
          <div className="floor-success-content">
            <div className="floor-success-checkmark">
              <svg
                width="45"
                height="45"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#25C35E"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h3>Success!</h3>
            <p>Floor assignments saved successfully</p>
          </div>
        </div>
      )}

      {/* Error Popup */}
      {showErrorPopup && (
        <div className="floor-error-popup">
          <div className="floor-error-content">
            <div className="floor-error-cross">
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="3">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Error!</h3>
            <p>{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Restaurant Settings */}
      <div className="floor-settings-card">
        <div className="floor-settings-row">
          <label>Total Tables in Restaurant:</label>
          <div className="floor-total-tables">
            {totalTables} tables
          </div>
        </div>
      </div>

      {/* Floor Assignments */}
      <div className="floor-assignments">
        <div className="floor-header">
          <h4>Assign Floors to Tables</h4>
          <button className="floor-add-btn" onClick={addFloor}>
            + New Floor
          </button>
        </div>

        {floorAssignments.map((floor, idx) => (
          <div key={idx} className="floor-card">
            <div className="floor-card-header">
              <div className="floor-summary">
                Tables: {floor.fromTable} - {floor.toTable} ({Math.max(0, floor.toTable - floor.fromTable + 1)} tables)
              </div>
              {floorAssignments.length > 1 && (
                <button 
                  className="floor-remove-btn" 
                  onClick={() => removeFloor(idx)}
                  title="Remove floor"
                >
                  ×
                </button>
              )}
            </div>

            <div className="floor-name-row">
              <label>Floor Name:</label>
              <input
                type="text"
                value={floor.floorName}
                onChange={(e) => updateFloorAssignment(idx, 'floorName', e.target.value)}
                placeholder={`Floor ${idx + 1}`}
                className="fm-name-input"
              />
            </div>

            <div className="floor-range-row">
              <div className="floor-range-group">
                <label>From Table:</label>
                <input
                  type="number"
                  max={totalTables}
                  value={floor.fromTable}
                  onChange={(e) => updateFloorAssignment(idx, 'fromTable', e.target.value)}
                  className="fm-range-input"
                />
              </div>

              <div className="floor-range-group">
                <label>To Table:</label>
                <input
                  type="number"
                  max={totalTables}
                  value={floor.toTable}
                  onChange={(e) => updateFloorAssignment(idx, 'toTable', e.target.value)}
                  className="fm-range-input"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="floor-button-group">
        <button
          type="button"
          onClick={cancelChanges}
          className="floor-cancel-btn"
          disabled={loading}
        >
          Cancel
        </button>
        <button 
          onClick={saveFloorAssignments} 
          className="floor-save-btn"
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

export default FloorManagement;
